/**
 * Tab switcher for the contact-form-card block's optional Calendly tab.
 *
 * Toggles between the CF7 form panel and the Calendly embed panel using the
 * `[data-tab-target]`/`[data-tab-panel]` markup rendered by the block's PHP
 * template. Also lazily loads Calendly's assets only when the user opens the
 * Calendly tab or popup CTA.
 */

const CALENDLY_WIDGET_SCRIPT_ID = 'calendly-widget-script';
const CALENDLY_WIDGET_STYLE_ID = 'calendly-widget-style';
const CALENDLY_WIDGET_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_WIDGET_STYLE_URL = 'https://assets.calendly.com/assets/external/widget.css';

let calendlyAssetsPromise;

/**
 * Validate and normalize a candidate Calendly URL.
 *
 * @param {string} candidate Raw URL string.
 * @return {string} Safe Calendly URL or empty string.
 */
const getCalendlyUrl = ( candidate ) => {

	try {

		const nextUrl = new URL( String( candidate || '' ), window.location.href );

		if ( nextUrl.protocol !== 'https:' ) {

			return '';

		}

		if ( nextUrl.hostname !== 'calendly.com' && ! nextUrl.hostname.endsWith( '.calendly.com' ) ) {

			return '';

		}

		return nextUrl.toString();

	} catch {

		return '';

	}

};

/**
 * Load Calendly widget assets once.
 *
 * @return {Promise<void>} Resolves when the widget assets are available.
 */
const loadCalendlyAssets = () => {

	if ( window.Calendly?.initPopupWidget && window.Calendly?.initInlineWidget ) {

		return Promise.resolve();

	}

	if ( calendlyAssetsPromise ) {

		return calendlyAssetsPromise;

	}

	calendlyAssetsPromise = new Promise( ( resolve, reject ) => {

		if ( ! document.getElementById( CALENDLY_WIDGET_STYLE_ID ) ) {

			const style = document.createElement( 'link' );
			style.id = CALENDLY_WIDGET_STYLE_ID;
			style.rel = 'stylesheet';
			style.href = CALENDLY_WIDGET_STYLE_URL;
			document.head.appendChild( style );

		}

		const existingScript = document.getElementById( CALENDLY_WIDGET_SCRIPT_ID );

		if ( existingScript ) {

			existingScript.addEventListener( 'load', () => resolve(), { once: true } );
			existingScript.addEventListener( 'error', () => reject( new Error( 'Calendly widget failed to load.' ) ), { once: true } );

			if ( window.Calendly?.initPopupWidget && window.Calendly?.initInlineWidget ) {

				resolve();

			}

			return;

		}

		const script = document.createElement( 'script' );
		script.id = CALENDLY_WIDGET_SCRIPT_ID;
		script.src = CALENDLY_WIDGET_SCRIPT_URL;
		script.async = true;
		script.addEventListener( 'load', () => resolve(), { once: true } );
		script.addEventListener( 'error', () => reject( new Error( 'Calendly widget failed to load.' ) ), { once: true } );
		document.head.appendChild( script );

	} );

	return calendlyAssetsPromise;

};

/**
 * Initialize an inline Calendly widget for a panel on first activation.
 *
 * @param {HTMLElement|null} panel Tab panel element.
 * @return {Promise<void>} Resolves after the widget is initialized or skipped.
 */
const initCalendlyInlineWidget = async ( panel ) => {

	if ( ! panel || panel.dataset.calendlyInitialized === 'true' ) {

		return;

	}

	const embedTarget = panel.querySelector( '[data-calendly-url]' );
	const calendlyUrl = getCalendlyUrl( embedTarget?.dataset.calendlyUrl );

	if ( ! embedTarget || ! calendlyUrl ) {

		return;

	}

	await loadCalendlyAssets();

	if ( ! window.Calendly?.initInlineWidget ) {

		return;

	}

	embedTarget.innerHTML = '';
	window.Calendly.initInlineWidget( {
		url: calendlyUrl,
		parentElement: embedTarget,
	} );
	panel.dataset.calendlyInitialized = 'true';

};

/**
 * Wire up Calendly popup CTA links in a single card.
 *
 * @param {HTMLElement} card Contact form card element.
 * @return {void}
 */
const initCalendlyPopup = ( card ) => {

	const buttonsWrap = card.querySelector( '.b-contact-form-card__booking-cta-buttons[data-calendly-popup-url]' );

	if ( ! buttonsWrap || buttonsWrap.dataset.calendlyPopupInitialized === 'true' ) {

		return;

	}

	buttonsWrap.dataset.calendlyPopupInitialized = 'true';

	buttonsWrap.addEventListener( 'click', async ( event ) => {

		const trigger = event.target.closest( 'a' );

		if ( ! trigger ) {

			return;

		}

		const calendlyUrl = getCalendlyUrl( buttonsWrap.dataset.calendlyPopupUrl || trigger.href );

		if ( ! calendlyUrl ) {

			return;

		}

		event.preventDefault();

		try {

			await loadCalendlyAssets();

			if ( window.Calendly?.initPopupWidget ) {

				window.Calendly.initPopupWidget( { url: calendlyUrl } );
				return;

			}

		} catch {}

		window.open( calendlyUrl, '_blank', 'noopener,noreferrer' );

	} );

};

/**
 * Wire up a single contact-form-card's tab switcher.
 *
 * @param {HTMLElement} tabs Element carrying [role="tablist"].
 * @return {void}
 */
const initTabs = ( tabs ) => {

	const card = tabs.closest( '.b-contact-form-card' );
	const tabButtons = Array.from( tabs.querySelectorAll( '[data-tab-target]' ) );

	if ( ! card || ! tabButtons.length ) {

		return;

	}

	/**
	 * Toggle the active tab and its associated panel.
	 *
	 * @param {string}  target      Active tab target key.
	 * @param {boolean} shouldFocus Whether to move focus to the active tab.
	 * @return {void}
	 */
	const setActiveTab = ( target, shouldFocus = false ) => {

		tabButtons.forEach( ( tab ) => {

			const isActive = tab.dataset.tabTarget === target;

			tab.classList.toggle( 'is-active', isActive );
			tab.setAttribute( 'aria-selected', String( isActive ) );
			tab.tabIndex = isActive ? 0 : -1;

			if ( shouldFocus && isActive ) {

				tab.focus();

			}

		} );

		card.querySelectorAll( '[data-tab-panel]' ).forEach( ( panel ) => {

			panel.hidden = panel.dataset.tabPanel !== target;

		} );

		if ( target === 'calendly' ) {

			initCalendlyInlineWidget( card.querySelector( '[data-tab-panel="calendly"]' ) ).catch( () => {

				const calendlyPanel = card.querySelector( '[data-tab-panel="calendly"]' );

				if ( calendlyPanel ) {

					calendlyPanel.dataset.calendlyInitialized = 'error';

				}

			} );

		}

	};

	/**
	 * Handle WAI-ARIA tabs keyboard navigation.
	 *
	 * @param {KeyboardEvent} event        Keyboard event.
	 * @param {number}        currentIndex Currently focused tab index.
	 * @return {void}
	 */
	const handleKeydown = ( event, currentIndex ) => {

		const nextIndexByKey = {
			ArrowRight: () => ( currentIndex + 1 ) % tabButtons.length,
			ArrowLeft: () => ( currentIndex - 1 + tabButtons.length ) % tabButtons.length,
			Home: () => 0,
			End: () => tabButtons.length - 1,
		};

		if ( ! nextIndexByKey[ event.key ] ) {

			return;

		}

		event.preventDefault();
		setActiveTab( tabButtons[ nextIndexByKey[ event.key ]() ].dataset.tabTarget, true );

	};

	tabButtons.forEach( ( tab, index ) => {

		tab.addEventListener( 'click', () => {

			setActiveTab( tab.dataset.tabTarget );

		} );

		tab.addEventListener( 'keydown', ( event ) => handleKeydown( event, index ) );

	} );

	setActiveTab( tabButtons[ 0 ].dataset.tabTarget );
	initCalendlyPopup( card );

};

/**
 * Wire up every contact-form-card tab switcher found on the page.
 *
 * @return {void}
 */
const initContactFormCardTabs = () => {

	document.querySelectorAll( '.b-contact-form-card__tabs' ).forEach( initTabs );

};

document.addEventListener( 'DOMContentLoaded', initContactFormCardTabs );
